import { type AnalysisStateRepository, AnalysisState, type UserRepository, AnalysisStateValue } from '@telespot/domain';

// --- Input DTO ---

export interface GetCaseAnalysisSummaryInput {
  caseId: string;
  currentUserId: string;
  createdById: string;
}

// --- Output DTOs ---

export interface AnalysisCaseUserData {
  id: string;
  username: string;
}

export interface CaseAnalysisSummary {
  state: AnalysisStateValue;
  createdBy: AnalysisCaseUserData;
  analysing: AnalysisCaseUserData[];
  analyzed: AnalysisCaseUserData[];
}

// --- Error ---

/**
 * Thrown when retrieving the case analysis summary fails (e.g. repository error).
 */
export class GetCaseAnalysisSummaryQueryError extends Error {
  constructor(message = 'Failed to get case analysis summary', public readonly cause?: unknown) {
    super(message);
    this.name = 'GetCaseAnalysisSummaryQueryError';
    Object.setPrototypeOf(this, GetCaseAnalysisSummaryQueryError.prototype);
  }
}

// --- Interface ---

/**
 * Query that returns the analysis summary for a single case: its derived state
 * and the analyst user data.
 */
export interface IGetCaseAnalysisSummaryQuery {
  /**
   * Get the analysis summary for a case.
   * @param input Case id, current user id, and the creator's user id.
   * @returns The analysis summary.
   * @throws GetCaseAnalysisSummaryQueryError when the operation fails.
   */
  execute(input: GetCaseAnalysisSummaryInput): Promise<CaseAnalysisSummary>;
}

// --- Implementation ---

export class GetCaseAnalysisSummaryQuery implements IGetCaseAnalysisSummaryQuery {
  constructor(
    private readonly analysisStateRepository: AnalysisStateRepository,
    private readonly userRepository: UserRepository
  ) {}

  public async execute(input: GetCaseAnalysisSummaryInput): Promise<CaseAnalysisSummary> {
    try {
      const { caseId, currentUserId, createdById } = input;

      // Collect all analysis states for this case
      const allStates: AnalysisState[] = [];
      for await (const batch of this.analysisStateRepository.findStatesForCase(caseId)) {
        allStates.push(...batch);
      }

      // Find current user's state (default to 'pending' if no state exists)
      const currentUserAnalysisState = allStates.find((s) => s.analizerId === currentUserId);
      const currentUserState: AnalysisStateValue = currentUserAnalysisState?.state ?? AnalysisStateValue.PENDING;

      // Build analysing and analyzed user lists
      const analysingStates = allStates.filter((s) => s.state === AnalysisStateValue.IN_PROGRESS);
      const analyzedStates = allStates.filter((s) => s.state === AnalysisStateValue.ANALYZED);

      // Look up user data for analysing/analyzed users + createdBy in ONE
      // query. Fetching each id individually with getById produced a 404 per
      // id the caller can't access (e.g. a case creator who's no longer in the
      // org — scoped out by the _User afterFind hook), spamming the console.
      // A single batch find just omits inaccessible ids instead.
      const userIdsToLookup = [
        ...new Set<string>([
          createdById,
          ...analysingStates.map((s) => s.analizerId),
          ...analyzedStates.map((s) => s.analizerId),
        ]),
      ];

      const userMap = new Map<string, AnalysisCaseUserData>();
      try {
        const users = await this.userRepository.getByIds(userIdsToLookup);
        for (const user of users) {
          userMap.set(user.id, { id: user.id, username: user.username });
        }
      } catch {
        // Leave the map empty; callers fall back to 'Unknown'.
      }

      // Build the result
      const createdBy = userMap.get(createdById) ?? { id: createdById, username: 'Unknown' };
      const analysing = analysingStates
        .map((s) => userMap.get(s.analizerId))
        .filter((u): u is AnalysisCaseUserData => u !== undefined);
      const analyzed = analyzedStates
        .map((s) => userMap.get(s.analizerId))
        .filter((u): u is AnalysisCaseUserData => u !== undefined);

      return { state: currentUserState, createdBy, analysing, analyzed };
    } catch (error) {
      throw new GetCaseAnalysisSummaryQueryError('Failed to get case analysis summary', error);
    }
  }
}
